'use client'

import getDiscordServerChannels from '@/actions/get-discord-server-channels'
import FormSelectInput from '@/components/forms/inputs/form-select-input'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useEffect, useMemo } from 'react'
import useSWR from 'swr'

export default function CommunityDiscordChannelInput({ form, className }) {
  // Get the currently selected Discord server ID from the form
  const selectedServerId = form.watch('discordServerId')

  // Only fetch channels if a server is selected
  const {
    data: channelData,
    isLoading,
    error,
    mutate,
  } = useSWR(
    selectedServerId && selectedServerId !== 'n/a' ? selectedServerId : null,
    getDiscordServerChannels,
  )

  // Refresh channels list after form submission
  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      mutate()
    }
  }, [form.formState.isSubmitSuccessful, mutate])

  // Map channels to select options
  const channelOptions = useMemo(() => {
    if (!channelData?.channels || !Array.isArray(channelData.channels))
      return []

    return channelData.channels.map((channel) => ({
      value: channel.id,
      label: channel.name,
    }))
  }, [channelData])

  // If no server is selected, don't render the channel selector
  if (!selectedServerId || selectedServerId === 'n/a') {
    return null
  }

  // If the bot has access, show the select input
  return (
    <div className="flex flex-col gap-1">
      <FormSelectInput
        name="notificationChannelId"
        options={channelOptions}
        emptyOption={{
          value: 'n/a',
          label: 'No text channels found',
          disabled: true,
        }}
        disabled={isLoading || !channelData.botHasAccess || error}
        placeholder="Select a Discord channel"
        label="Discord Channel"
        description="Select a text channel from your Discord server for notifications."
        btnClassName={cn('w-full justify-between', className)}
        {...form}
      />
      {error && (
        <div className="text-xs text-error-500">
          Failed to load channels. Please try again.
        </div>
      )}
      {!isLoading && !channelData?.botHasAccess && (
        <>
          <p className="mt-2 font-inter text-p-1 text-warning-400">
            We've detected that our bot does not have access to your server yet.
            <br />
          </p>
          <Link
            className="font-inter text-p-1 text-warning-400 underline underline-offset-2"
            href="https://discord.com/oauth2/authorize?client_id=953211455023513610"
          >
            Add it here!
          </Link>
        </>
      )}
    </div>
  )
}
